import { ArrowRightIcon, BodyText, Card, Heading } from '@farol-ds/react'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import { GA4ClickEvent, GA4ViewItemListEvent } from '@jusbrasil-web/shared-ga4-events'
import { useOnScroll } from '@jusbrasil-web/shared-hooks'
import { useCallback, useRef } from 'react'

import styles from './follow-up-questions.module.scss'

const questions = [
  'Quais são as principais divergências jurisprudenciais sobre essa tese?',
  'Como encontrar teses contrárias para fundamentar uma contra-argumentação?',
  'Qual a base legal que fundamenta essa tese?',
]

export interface FollowUpQuestionsProps {
  clusterId: string
  clusterLabel: string
}

export function FollowUpQuestions(props: FollowUpQuestionsProps) {
  const { clusterId, clusterLabel } = props

  const { sendGA4Event } = useGA4Dispatcher()
  const componentRef = useRef<HTMLDivElement>(null)
  const hasDispatched = useRef<boolean>(false)

  const isInViewport = useCallback((element: HTMLElement) => {
    const rect = element.getBoundingClientRect()
    return rect.top >= 0 && rect.bottom <= window.innerHeight
  }, [])

  const dispatchGA4Event = useCallback(() => {
    if (hasDispatched.current || !componentRef.current) return

    if (isInViewport(componentRef.current)) {
      sendGA4Event(
        new GA4ViewItemListEvent({
          renders: questions.map((question) => ({
            item_variant: 'follow_up_question',
            item_name: 'cluster_theses_jurisprudence',
            item_brand: 'result_snippet',
            item_id: `${clusterId}:${question}`,
          })),
        })
      )
      hasDispatched.current = true
    }
  }, [sendGA4Event, isInViewport, clusterId])

  useOnScroll({
    delay: 500,
    callback: dispatchGA4Event,
  })

  const onQuestionClick = (question: string) => {
    sendGA4Event(
      new GA4ClickEvent({
        componentName: 'cluster_theses_jurisprudence',
        componentClass: 'result_snippet',
        selectedOption: 'follow_up_question',
        resourceId: `${clusterId}:${question}`,
      })
    )
  }

  const getURLChatJusia = (question: string) => {
    const URL_CHAT_JUSIA = 'https://ia.jusbrasil.com.br/conversa'
    const questionText = `${clusterLabel}\n\n${question}`
    return `${URL_CHAT_JUSIA}?q=${encodeURIComponent(questionText)}&send`
  }

  return (
    <div ref={componentRef} className={styles.container}>
      <Heading size="sm" className={styles.title} asChild>
        <h2>O que você gostaria de saber sobre esse assunto?</h2>
      </Heading>

      <div className={styles.questionsContainer}>
        {questions.map((question, index) => (
          <Card
            key={index}
            className={styles.questionCard}
            onClick={() => onQuestionClick(question)}
            data-ga4-component-option="static_reference"
            data-ga4-resource-id={question}
            disablePadding
            data-testid="follow-up-question"
            asChild
          >
            <a href={getURLChatJusia(question)}>
              <BodyText size="md" asChild>
                <h3>{question}</h3>
              </BodyText>
              <ArrowRightIcon size="sm" className={styles.arrowIcon} />
            </a>
          </Card>
        ))}
      </div>
    </div>
  )
}
